import {KTSVG} from '../../../_metronic/helpers'
import {FC, useEffect, useState, forwardRef, useImperativeHandle} from 'react'
import {Modal} from 'react-bootstrap'
import {useTodaysCashFloat, useCashFloatHistory} from '../../contextProviders/Atomqueryprovider'
import {useAuth} from '../../../app/modules/auth'
import {useMutation, useQueryClient} from 'react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import relativeTime from 'dayjs/plugin/relativeTime'
import calendar from 'dayjs/plugin/calendar'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)
dayjs.extend(calendar)
dayjs.extend(localizedFormat)

type Props = {
  show: boolean
  handleClose: () => void
  isadmin: boolean
  ref?: any
}

const CURRENCY_APP = process.env.REACT_APP_CURRENCY || 'USD'
const OPENING_TIME = process.env.REACT_APP_OPENING_TIME || '08:00'

// Helper function to safely convert to number
const safeToNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(value)
  return isNaN(num) ? 0 : num
}

export const Cashfloat: FC<Props> = forwardRef((props, ref) => {
  // state
  const [submitCashFloat, setSubmitCashFloat] = useState(false)
  const [floatAction, setFloatAction] = useState<'view' | 'open' | 'close' | 'update'>('view')
  const [openingBalance, setOpeningBalance] = useState<number>(0)
  const [closingBalance, setClosingBalance] = useState<number>(0)
  const [notes, setNotes] = useState('')
  const [currentFloat, setCurrentFloat] = useState<any>(null)
  const [businessDate, setBusinessDate] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [previousFloat, setPreviousFloat] = useState<number>(0)
  const [additionalFloat, setAdditionalFloat] = useState<number>(0)

  const {currentUser} = useAuth()
  const queryClient = useQueryClient()

  // ——————— Business‐date logic ———————

  // 1) current business date calculation
  const getBusinessDate = (): string => {
    try {
      const now = dayjs()
      const today = now.format('YYYY-MM-DD')
      const todayOpeningTime = dayjs(`${today} ${OPENING_TIME}`)

      return now.isBefore(todayOpeningTime) ? now.subtract(1, 'day').format('YYYY-MM-DD') : today
    } catch (error) {
      console.error('Error calculating business date:', error)
      return dayjs().format('YYYY-MM-DD')
    }
  }

  // 2) float's business date from its created_at
  const getBusinessDateFromTimestamp = (createdAt: string): string => {
    try {
      const createdDate = dayjs(createdAt)
      const createdDay = createdDate.format('YYYY-MM-DD')
      const createdDayOpeningTime = dayjs(`${createdDay} ${OPENING_TIME}`)

      return createdDate.isBefore(createdDayOpeningTime)
        ? createdDate.subtract(1, 'day').format('YYYY-MM-DD')
        : createdDay
    } catch (error) {
      console.error('Error calculating business date from timestamp:', error)
      return ''
    }
  }

  // 3) is today the current business day?
  const isCurrentBusinessDay = () => {
    if (!businessDate) return false
    return businessDate === getBusinessDate()
  }

  // 4) display helpers
  const getBusinessDateDisplay = () => {
    if (!businessDate) return 'Loading...'

    const today = dayjs().format('YYYY-MM-DD')
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD')

    if (businessDate === today) return 'Today'
    if (businessDate === yesterday) return 'Yesterday (Extended Business Day)'
    return dayjs(businessDate).format('MMM DD, YYYY')
  }

  const getBusinessStartDateTime = () => {
    if (!businessDate) return null
    return dayjs(`${businessDate} ${OPENING_TIME}`)
  }

  const getBusinessEndDateTime = () => {
    const start = getBusinessStartDateTime()
    return start ? start.add(24, 'hour') : null
  }

  const getBusinessHoursDisplay = () => {
    const start = getBusinessStartDateTime()
    const end = getBusinessEndDateTime()

    if (!start || !end) return 'Loading business hours...'

    return `${start.format('MMM DD, YYYY hh:mm A')} – ${end.format('MMM DD, YYYY hh:mm A')}`
  }

  // ONLY make API call when businessDate is ready and valid
  const {
    isLoading: todaysFloatLoading,
    isSuccess: todaysFloatSuccess,
    data: todaysFloatData,
    refetch: todaysFloatRefetch,
  } = useTodaysCashFloat(Number(currentUser?.user_id), businessDate, {
    enabled: !!businessDate && props.show,
    onSuccess: () => setIsLoading(false),
    onError: () => setIsLoading(false),
  })

  // Fetch last closed float balance
  const {data: floatHistoryData, isSuccess: floatHistorySuccess} = useCashFloatHistory(
    Number(currentUser?.user_id),
    0,
    5, // Check recent 5 records to find a closed one
    dayjs().subtract(30, 'day').toDate(),
    dayjs().toDate(),
    'created_at',
    'DESC',
    '%'
  )
  // ——— expose reset via ref ———
  useImperativeHandle(ref, () => ({
    Resetmodalcashfloatvalues() {
      setSubmitCashFloat(false)
      setOpeningBalance(0)
      setClosingBalance(0)
      setNotes('')
      setFloatAction('view')
      setBusinessDate('')
      setCurrentFloat(null)
      setIsLoading(true)
      setPreviousFloat(0)
      setAdditionalFloat(0)
    },
  }))

  // STEP 1: When modal opens, calculate and set business date
  useEffect(() => {
    if (props.show && !businessDate) {
      const calculatedDate = getBusinessDate()
      setBusinessDate(calculatedDate)
    }

    if (!props.show) {
      setBusinessDate('')
      setSubmitCashFloat(false)
      setOpeningBalance(0)
      setClosingBalance(0)
      setNotes('')
      setFloatAction('view')
      setCurrentFloat(null)
      setIsLoading(true)
      setPreviousFloat(0)
      setAdditionalFloat(0)
    }
  }, [props.show])

  // STEP 2: When data is fetched successfully, process and populate state
  useEffect(() => {
    if (!props.show || !businessDate || !todaysFloatSuccess) return

    setIsLoading(false)

    // FIX: Properly handle data structure
    let allFloats = []

    // Handle different possible data structures
    if (Array.isArray(todaysFloatData)) {
      allFloats = todaysFloatData
    } else if (todaysFloatData && Array.isArray(todaysFloatData.data)) {
      allFloats = todaysFloatData.data
    } else if (todaysFloatData && typeof todaysFloatData === 'object') {
      allFloats = Object.values(todaysFloatData).filter(
        (item) => item && typeof item === 'object' && item.created_at
      )
    }

    // Filter floats for current business date
    const todaysFloats = allFloats.filter((float: any) => {
      if (!float?.created_at) return false
      const floatBusinessDate = getBusinessDateFromTimestamp(float.created_at)
      return floatBusinessDate === businessDate
    })

    if (todaysFloats.length > 0) {
      const mostRecent = todaysFloats.sort(
        (a: any, b: any) => dayjs(b.created_at).unix() - dayjs(a.created_at).unix()
      )[0]

      setCurrentFloat(mostRecent)

      const openingBal = safeToNumber(mostRecent.opening_balance)
      const closingBal = safeToNumber(mostRecent.closing_balance)

      setOpeningBalance(openingBal)
      setClosingBalance(closingBal)
      setNotes(mostRecent.notes || '')

      if (mostRecent.status === 'OPEN') {
        setFloatAction('close')
      } else {
        setFloatAction('view')
      }
    } else {
      setCurrentFloat(null)
      setOpeningBalance(0)
      setClosingBalance(0)
      setNotes('')
      setFloatAction(isCurrentBusinessDay() || props.isadmin ? 'open' : 'view')
    }
  }, [props.show, businessDate, todaysFloatSuccess, todaysFloatData])

  // STEP 3: Handle history data to pre-fill previous float
  useEffect(() => {
    if (!props.show || floatAction !== 'open' || !floatHistorySuccess || !floatHistoryData) return

    let history = []
    if (Array.isArray(floatHistoryData)) {
      history = floatHistoryData
    } else if (floatHistoryData && Array.isArray(floatHistoryData.data)) {
      history = floatHistoryData.data
    }

    if (history.length > 0) {
      // The first record should be the most recent one due to DESC order
      const lastFloat = history[0]
      if (lastFloat) {
        // Use closing balance if available, otherwise opening balance
        const lastVal =
          lastFloat.status === 'CLOSED'
            ? safeToNumber(lastFloat.closing_balance)
            : safeToNumber(lastFloat.opening_balance)
        setPreviousFloat(lastVal)
      }
    }
  }, [props.show, floatAction, floatHistorySuccess, floatHistoryData])

  // ——— handlers ———
  const handleOpeningBalanceChange = (e: any) => {
    const value = safeToNumber(e.target.value)
    setOpeningBalance(value)
  }

  const handleClosingBalanceChange = (e: any) => {
    const value = safeToNumber(e.target.value)
    setClosingBalance(value)
  }

  const handleNotesChange = (e: any) => setNotes(e.target.value)

  // ——— mutations ———
  const {mutateAsync: openCashFloatMutateAsync} = useMutation(async (floatData: any) => {
    const {openCashFloat} = await import('../../apis/Loadalltabledata')
    return openCashFloat(floatData)
  })
  const {mutateAsync: closeCashFloatMutateAsync} = useMutation(async (floatData: any) => {
    const {closeCashFloat} = await import('../../apis/Loadalltabledata')
    return closeCashFloat(floatData)
  })
  const {mutateAsync: updateCashFloatMutateAsync} = useMutation(async (floatData: any) => {
    const {updateCashFloat} = await import('../../apis/Loadalltabledata')
    return updateCashFloat(floatData)
  })

  // ——— submit action ———
  const submitCashFloatAction = async () => {
    if (!businessDate) {
      console.error('Cannot submit: business date not ready')
      return
    }

    setSubmitCashFloat(true)
    try {
      switch (floatAction) {
        case 'open': {
          const payload = {
            user_id: Number(currentUser?.user_id),
            opening_balance: Number(previousFloat) + Number(additionalFloat),
            date_recorded: businessDate,
            notes,
          }
          await openCashFloatMutateAsync(payload, {
            onSuccess: () => {
              queryClient.invalidateQueries(['todayscashfloat'])
              queryClient.invalidateQueries(['cashfloat'])
              setSubmitCashFloat(false)
              props.handleClose()
            },
            onError: () => {
              setSubmitCashFloat(false)
              props.handleClose()
            },
          })
          break
        }
        case 'close': {
          const payload = {
            float_id: currentFloat?.float_id,
            user_id: Number(currentUser?.user_id),
            closing_balance: Number(closingBalance),
            notes,
            date_recorded: businessDate,
          }
          await closeCashFloatMutateAsync(payload, {
            onSuccess: () => {
              queryClient.invalidateQueries(['todayscashfloat'])
              queryClient.invalidateQueries(['cashfloat'])
              setSubmitCashFloat(false)
              props.handleClose()
            },
            onError: () => {
              setSubmitCashFloat(false)
              props.handleClose()
            },
          })
          break
        }
        case 'update': {
          const payload = {
            float_id: currentFloat?.float_id,
            user_id: Number(currentUser?.user_id),
            opening_balance: Number(openingBalance),
            closing_balance: currentFloat?.status === 'CLOSED' ? Number(closingBalance) : null,
            notes,
          }
          await updateCashFloatMutateAsync(payload, {
            onSuccess: () => {
              queryClient.invalidateQueries(['todayscashfloat'])
              queryClient.invalidateQueries(['cashfloat'])
              setSubmitCashFloat(false)
              props.handleClose()
            },
            onError: () => {
              setSubmitCashFloat(false)
              props.handleClose()
            },
          })
          break
        }
      }
    } catch (err) {
      console.error(err)
      setSubmitCashFloat(false)
    }
  }

  // ——— UI helpers ———
  const addCommas = (n: number) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const getActionTitle = () => {
    switch (floatAction) {
      case 'open':
        return 'Open Cash Float'
      case 'close':
        return 'Close Cash Float'
      case 'update':
        return 'Update Cash Float'
      default:
        return 'Cash Float Status'
    }
  }

  const getButtonText = () => {
    switch (floatAction) {
      case 'open':
        return 'Open Float'
      case 'close':
        return 'Close Float'
      case 'update':
        return 'Update Float'
      default:
        return 'View'
    }
  }

  const canPerformAction = (action: string) => {
    if (!businessDate) return false

    if (currentFloat?.status === 'OPEN') {
      if (action === 'close') return true
      if (action === 'update' && props.isadmin) return true
      return false
    }
    if (!currentFloat && action === 'open') {
      return isCurrentBusinessDay() || props.isadmin
    }
    if (currentFloat && action === 'update' && props.isadmin) return true
    return false
  }

  const isFieldEditable = (field: 'opening' | 'closing' | 'notes') => {
    if (floatAction === 'update' && props.isadmin) {
      return true
    }

    if (field === 'opening') {
      return floatAction === 'open'
    }
    if (field === 'closing') {
      return floatAction === 'close'
    }
    if (field === 'notes') {
      return floatAction !== 'view'
    }
    return false
  }

  // Show loading state ONLY while business date is being calculated
  if (props.show && !businessDate) {
    return (
      <Modal
        className='modal fade'
        data-backdrop='static'
        show={props.show}
        centered
        dialogClassName='modal-lg'
        onHide={props.handleClose}
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <h3 className='fw-bolder mb-1'>Cash Float</h3>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              onClick={props.handleClose}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
            </div>
          </div>
          <div className='modal-body text-center p-10'>
            <div className='spinner-border text-primary mb-4' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='text-muted'>Calculating business date...</p>
          </div>
        </div>
      </Modal>
    )
  }

  // Render main modal content
  return (
    <Modal
      className='modal fade'
      data-backdrop='static'
      show={props.show}
      centered
      dialogClassName='modal-lg'
      onHide={props.handleClose}
    >
      <div className='modal-content'>
        {/* Header */}
        <div className='modal-header'>
          <div>
            <h3 className='fw-bolder mb-1'>{getActionTitle()}</h3>
            <p className='text-muted fs-7 mb-0'>
              Business Date: <span className='fw-bold'>{getBusinessDateDisplay()}</span>
              {!isCurrentBusinessDay() && (
                <span className='badge badge-light-warning ms-2'>Extended Hours</span>
              )}
              <br />
              <small className='text-muted'>
                Business hours:
                <b> {getBusinessHoursDisplay()}</b>
              </small>
            </p>
          </div>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={props.handleClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>

        {/* Body */}
        <div className='modal-body'>
          {/* Loading state for data */}
          {isLoading || todaysFloatLoading ? (
            <div className='text-center p-5'>
              <div className='spinner-border text-primary mb-3' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
              <p className='text-muted'>Loading cash float data...</p>
            </div>
          ) : (
            <>
              {/* Extended‐hours alert */}
              {!isCurrentBusinessDay() && (
                <div className='alert alert-warning d-flex align-items-center mb-5'>
                  <KTSVG
                    path='/media/icons/duotune/general/gen044.svg'
                    className='svg-icon-2hx svg-icon-warning me-4'
                  />
                  <div className='d-flex flex-column'>
                    <h6 className='mb-1'>Extended Business Hours</h6>
                    <span>
                      You're operating before {OPENING_TIME} – transactions will be recorded for{' '}
                      {getBusinessDateDisplay()}
                    </span>
                    {currentFloat?.status === 'OPEN' && (
                      <span className='text-danger fw-bold mt-1'>
                        ⚠️ You have an open cash float that must be closed first
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Current Float Status */}
              {currentFloat && (
                <div className='card bg-light-info border-0 mb-7'>
                  <div className='card-body p-4'>
                    <h6 className='fw-bold text-info mb-3'>Current Float Status</h6>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='d-flex justify-content-between mb-2'>
                          <span className='text-muted fw-bold'>Status:</span>
                          <span
                            className={`badge ${
                              currentFloat.status === 'OPEN'
                                ? 'badge-light-success'
                                : 'badge-light-primary'
                            }`}
                          >
                            {currentFloat.status}
                          </span>
                        </div>
                        <div className='d-flex justify-content-between mb-2'>
                          <span className='text-muted fw-bold'>Opening:</span>
                          <span className='fw-bold'>
                            {addCommas(safeToNumber(currentFloat.opening_balance))} {CURRENCY_APP}
                          </span>
                        </div>
                        {currentFloat.created_at && (
                          <div className='d-flex justify-content-between'>
                            <span className='text-muted fw-bold'>Created:</span>
                            <span className='fw-bold'>
                              {dayjs(currentFloat.created_at).format('MMM DD, h:mm A')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className='col-md-6'>
                        {currentFloat.status === 'CLOSED' && (
                          <>
                            <div className='d-flex justify-content-between mb-2'>
                              <span className='text-muted fw-bold'>Closing:</span>
                              <span className='fw-bold'>
                                {addCommas(safeToNumber(currentFloat.closing_balance))}{' '}
                                {CURRENCY_APP}
                              </span>
                            </div>
                            <div className='d-flex justify-content-between'>
                              <span className='text-muted fw-bold'>Variance:</span>
                              <span
                                className={`fw-bold ${
                                  safeToNumber(currentFloat.variance) >= 0
                                    ? 'text-success'
                                    : 'text-danger'
                                }`}
                              >
                                {safeToNumber(currentFloat.variance) >= 0 ? '+' : ''}
                                {addCommas(safeToNumber(currentFloat.variance))} {CURRENCY_APP}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!currentFloat && floatAction === 'view' && (
                <div className='alert alert-secondary mb-7'>
                  <div className='d-flex align-items-center'>
                    <KTSVG
                      path='/media/icons/duotune/abstract/abs021.svg'
                      className='svg-icon-2hx svg-icon-primary me-4'
                    />
                    <div>
                      <h6 className='mb-1'>No Cash Float Opened</h6>
                      <span>
                        {isCurrentBusinessDay() || props.isadmin
                          ? 'Click "Open Float" to start the day\'s cash management'
                          : 'Cash float can only be opened during current business hours'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Form */}
              <form className='form'>
                {floatAction === 'open' ? (
                  <>
                    <div className='fv-row mb-7'>
                      <label className='fw-bold fs-6 mb-2 required'>
                        Previous Day's Closing Float ({CURRENCY_APP})
                      </label>
                      <div className='input-group input-group-solid input-group-lg'>
                        <input
                          placeholder='0.00'
                          className='form-control form-control-solid'
                          type='number'
                          step='0.01'
                          autoComplete='off'
                          value={previousFloat || ''}
                          onChange={(e) => setPreviousFloat(safeToNumber(e.target.value))}
                        />
                        <span className='input-group-text'>{CURRENCY_APP}</span>
                      </div>
                      <div className='form-text text-muted'>
                        Automatically fetched from the last closed float record
                      </div>
                    </div>
                    <div className='fv-row mb-7'>
                      <label className='fw-bold fs-6 mb-2'>
                        Additional Cash Float ({CURRENCY_APP})
                      </label>
                      <div className='input-group input-group-solid input-group-lg'>
                        <input
                          placeholder='0.00'
                          className='form-control form-control-solid'
                          type='number'
                          step='0.01'
                          autoComplete='off'
                          value={additionalFloat || ''}
                          onChange={(e) => setAdditionalFloat(safeToNumber(e.target.value))}
                        />
                        <span className='input-group-text'>{CURRENCY_APP}</span>
                      </div>
                      <div className='form-text text-muted'>
                        New cash being added to today's float
                      </div>
                    </div>
                    <div className='card bg-light-success border-0 mb-7'>
                      <div className='card-body p-4 d-flex justify-content-between align-items-center'>
                        <span className='fw-bolder text-success fs-6'>Total Opening Balance:</span>
                        <span className='fw-bolder fs-3 text-success'>
                          {addCommas(previousFloat + additionalFloat)} {CURRENCY_APP}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className='fv-row mb-7'>
                    <label
                      className={`fw-bold fs-6 mb-2 ${
                        isFieldEditable('opening') ? 'required' : ''
                      }`}
                    >
                      Opening Balance ({CURRENCY_APP})
                    </label>
                    <input
                      placeholder='0.00'
                      className='form-control form-control-lg form-control-solid'
                      type='number'
                      step='0.01'
                      autoComplete='off'
                      value={openingBalance || ''}
                      onChange={handleOpeningBalanceChange}
                      disabled={!isFieldEditable('opening')}
                    />
                  </div>
                )}

                {currentFloat &&
                  (currentFloat.status === 'CLOSED' ||
                    floatAction === 'close' ||
                    (floatAction === 'update' && props.isadmin)) && (
                    <div className='fv-row mb-7'>
                      <label
                        className={`fw-bold fs-6 mb-2 ${
                          isFieldEditable('closing') ? 'required' : ''
                        }`}
                      >
                        Closing Balance ({CURRENCY_APP})
                      </label>
                      <input
                        placeholder={floatAction === 'close' ? 'Enter actual cash counted' : '0.00'}
                        className='form-control form-control-lg form-control-solid'
                        type='number'
                        step='0.01'
                        autoComplete='off'
                        value={closingBalance || ''}
                        onChange={handleClosingBalanceChange}
                        disabled={!isFieldEditable('closing')}
                      />
                    </div>
                  )}

                <div className='fv-row mb-7'>
                  <label className='fw-bold fs-6 mb-2'>Notes</label>
                  {floatAction === 'view' && !isFieldEditable('notes') ? (
                    <div
                      className='form-control form-control-lg form-control-solid bg-light'
                      style={{minHeight: '60px'}}
                    >
                      {currentFloat?.notes || 'No notes added'}
                    </div>
                  ) : (
                    <textarea
                      className='form-control form-control-lg form-control-solid'
                      rows={3}
                      placeholder='Add any notes about the cash float...'
                      value={notes}
                      onChange={handleNotesChange}
                      disabled={!isFieldEditable('notes')}
                    />
                  )}
                </div>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <div className='modal-footer border-0 mt-n5 justify-content-start'>
          <div className='d-flex'>
            {!currentFloat && (
              <button
                type='button'
                className={`btn btn-sm me-2 ${
                  floatAction === 'open' ? 'btn-primary' : 'btn-light'
                }`}
                onClick={() => setFloatAction('open')}
                disabled={!canPerformAction('open') || isLoading || todaysFloatLoading}
              >
                Open Float
              </button>
            )}
            {currentFloat?.status === 'OPEN' && (
              <button
                type='button'
                className={`btn btn-sm me-2 ${
                  floatAction === 'close' ? 'btn-warning' : 'btn-light'
                }`}
                onClick={() => setFloatAction('close')}
                disabled={!canPerformAction('close') || isLoading || todaysFloatLoading}
              >
                Close Float
              </button>
            )}
            {currentFloat && props.isadmin && (
              <button
                type='button'
                className={`btn btn-sm me-2 ${floatAction === 'update' ? 'btn-info' : 'btn-light'}`}
                onClick={() => {
                  setFloatAction('update')
                  setOpeningBalance(safeToNumber(currentFloat.opening_balance))
                  setClosingBalance(safeToNumber(currentFloat.closing_balance))
                  setNotes(currentFloat.notes || '')
                }}
                disabled={!canPerformAction('update') || isLoading || todaysFloatLoading}
              >
                Update Float
              </button>
            )}
          </div>

          <div className='ms-auto d-flex'>
            <button
              type='button'
              className='btn btn-light-primary me-2'
              onClick={props.handleClose}
            >
              Cancel
            </button>
            {floatAction !== 'view' &&
              canPerformAction(floatAction) &&
              !isLoading &&
              !todaysFloatLoading && (
                <button
                  id='submit'
                  type='button'
                  className={`btn ${
                    floatAction === 'open'
                      ? 'btn-primary'
                      : floatAction === 'close'
                      ? 'btn-warning'
                      : 'btn-info'
                  }`}
                  onClick={submitCashFloatAction}
                  disabled={
                    submitCashFloat ||
                    !businessDate ||
                    (floatAction === 'open' && previousFloat + additionalFloat <= 0) ||
                    (floatAction === 'close' &&
                      (closingBalance === null ||
                        closingBalance === undefined ||
                        Number(closingBalance) < 0)) ||
                    (floatAction === 'update' && (!openingBalance || Number(openingBalance) <= 0))
                  }
                >
                  {!submitCashFloat && <span className='indicator-label'>{getButtonText()}</span>}
                  {submitCashFloat && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              )}
          </div>
        </div>
      </div>
    </Modal>
  )
})
